import { useCallback, useEffect, useRef, useState } from 'react'
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  type Node,
  type Edge,
  type NodeMouseHandler,
  type ReactFlowInstance,
} from '@xyflow/react'
import dagre from '@dagrejs/dagre'
import { Box, Skeleton, Text, Center, ActionIcon, Tooltip, Portal } from '@mantine/core'
import { IconMaximize, IconMinimize } from '@tabler/icons-react'
import '@xyflow/react/dist/style.css'
import { TopologyNode } from './TopologyNode'
import type { TopologyNode as TopologyNodeData, TopologyEdge as TopologyEdgeData } from '@/api/topology'

const nodeTypes = { topology: TopologyNode }

const NODE_WIDTH = 200
const NODE_HEIGHT = 70

function applyDagreLayout(nodes: Node[], edges: Edge[]): Node[] {
  const g = new dagre.graphlib.Graph()
  g.setDefaultEdgeLabel(() => ({}))

  const count = nodes.length
  const nodesep = count > 30 ? 20 : count > 15 ? 30 : 40
  const ranksep = count > 30 ? 180 : count > 15 ? 160 : 140

  g.setGraph({
    rankdir: 'LR',
    nodesep,
    ranksep,
    align: 'UL',
  })

  nodes.forEach((n) => {
    g.setNode(n.id, { width: NODE_WIDTH, height: NODE_HEIGHT })
  })

  edges.forEach((e) => {
    g.setEdge(e.source, e.target)
  })

  dagre.layout(g)

  return nodes.map((n) => {
    const pos = g.node(n.id)
    return {
      ...n,
      position: {
        x: pos.x - NODE_WIDTH / 2,
        y: pos.y - NODE_HEIGHT / 2,
      },
    }
  })
}

function buildFlowNodes(rawNodes: TopologyNodeData[]): Node[] {
  return rawNodes.map((n) => ({
    id: n.id,
    type: 'topology',
    position: n.position ?? { x: 0, y: 0 },
    data: n.data,
  }))
}

function buildFlowEdges(rawEdges: TopologyEdgeData[]): Edge[] {
  return rawEdges.map((e) => {
    const status = e.data?.status
    const isCritical = status === 'critical'
    const isWarning = status === 'warning'
    return {
      id: e.id,
      source: e.source,
      target: e.target,
      type: 'smoothstep',
      animated: false,
      style: {
        stroke: isCritical ? '#e03131' : isWarning ? '#f08c00' : '#495057',
        strokeWidth: isCritical ? 2 : 1.5,
        opacity: isCritical ? 1 : 0.6,
      },
      pathOptions: { borderRadius: 16 },
    }
  })
}

interface TopologyCanvasProps {
  nodes?: TopologyNodeData[]
  edges?: TopologyEdgeData[]
  loading?: boolean
  onNodeClick?: (nodeId: string, nodeData: TopologyNodeData['data']) => void
}

export function TopologyCanvas({ nodes: rawNodes, edges: rawEdges, loading, onNodeClick }: TopologyCanvasProps) {
  const [nodes, setNodes, onNodesChange] = useNodesState<Node>([])
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([])
  const [fullscreen, setFullscreen] = useState(false)
  const rfInstance = useRef<ReactFlowInstance | null>(null)
  const fitPending = useRef(false)

  // Layout nodes with dagre when raw data changes
  useEffect(() => {
    if (!rawNodes?.length) {
      setNodes([])
      setEdges([])
      return
    }

    const flowNodes = buildFlowNodes(rawNodes)
    const flowEdges = buildFlowEdges(rawEdges ?? [])
    const laidOutNodes = applyDagreLayout(flowNodes, flowEdges)

    setNodes(laidOutNodes)
    setEdges(flowEdges)
    fitPending.current = true
  }, [rawNodes, rawEdges, setNodes, setEdges])

  // Imperatively fitView AFTER nodes are set — solves the timing bug
  // where React Flow's fitView prop fires before dagre positions nodes
  useEffect(() => {
    if (!fitPending.current || !rfInstance.current || nodes.length === 0) return
    const timer = setTimeout(() => {
      rfInstance.current?.fitView({ padding: 0.15, maxZoom: 1, duration: 300 })
      fitPending.current = false
    }, 80)
    return () => clearTimeout(timer)
  }, [nodes])

  // Re-fit when entering/exiting fullscreen
  useEffect(() => {
    if (!rfInstance.current || nodes.length === 0) return
    const timer = setTimeout(() => {
      rfInstance.current?.fitView({ padding: 0.1, maxZoom: 1.2, duration: 300 })
    }, 100)
    return () => clearTimeout(timer)
  }, [fullscreen, nodes.length])

  const handleNodeClick: NodeMouseHandler = useCallback(
    (_event, node) => {
      if (onNodeClick) {
        onNodeClick(node.id, node.data as TopologyNodeData['data'])
      }
    },
    [onNodeClick]
  )

  // Close fullscreen on Escape
  useEffect(() => {
    if (!fullscreen) return
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setFullscreen(false)
    }
    document.addEventListener('keydown', handleKey)
    return () => document.removeEventListener('keydown', handleKey)
  }, [fullscreen])

  if (loading) {
    return <Skeleton height={500} radius="md" />
  }

  if (!rawNodes?.length) {
    return (
      <Center h={400}>
        <Text c="dimmed" size="sm">Nenhum recurso de topologia encontrado para este tenant.</Text>
      </Center>
    )
  }

  // Dynamic canvas height: scale with node count, cap at 800px inline
  const nodeCount = rawNodes?.length ?? 0
  const inlineHeight = Math.min(800, Math.max(500, nodeCount * 16))

  const canvas = (
    <Box
      style={{
        height: fullscreen ? '100%' : inlineHeight,
        borderRadius: fullscreen ? 0 : 8,
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onNodeClick={handleNodeClick}
        onInit={(instance) => { rfInstance.current = instance }}
        nodeTypes={nodeTypes}
        proOptions={{ hideAttribution: true }}
        minZoom={0.1}
        maxZoom={2.5}
      >
        <Background gap={20} size={1} color="var(--mantine-color-dark-4, #dee2e6)" />
        <Controls />
        <MiniMap
          nodeColor={(n) => {
            const status = (n.data as any)?.status
            if (status === 'critical') return '#e03131'
            if (status === 'warning') return '#f08c00'
            return '#2f9e44'
          }}
          maskColor="rgba(0,0,0,0.15)"
          pannable
          zoomable
        />
      </ReactFlow>

      {/* Fullscreen toggle */}
      <Tooltip label={fullscreen ? 'Sair (Esc)' : 'Tela cheia'} position="left">
        <ActionIcon
          variant="filled"
          color="dark"
          size="lg"
          onClick={() => setFullscreen((v) => !v)}
          style={{
            position: 'absolute',
            top: 10,
            right: 10,
            zIndex: 10,
            opacity: 0.85,
          }}
        >
          {fullscreen ? <IconMinimize size={18} /> : <IconMaximize size={18} />}
        </ActionIcon>
      </Tooltip>
    </Box>
  )

  if (fullscreen) {
    return (
      <Portal>
        <Box
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 1000,
            backgroundColor: 'var(--mantine-color-body)',
          }}
        >
          {canvas}
        </Box>
      </Portal>
    )
  }

  return canvas
}
